import { getGeolocationExtraFileds } from '@utils/Deal.utils';
import { checkUsedPromoCode } from '@utils/PromoCode.utils';
import { getSessionIdCookie, setSessionIdCookie } from '@utils/Session.utils';
import { useContext, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { IDeal, IConfigurableCarExtended } from 'src/interfaces/Car';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import FilterDuck from 'src/redux/filters/filter.duck';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import { Redux } from 'src/redux/redux.interface';
import { MySavedDealJourneyType } from 'src/redux/user/user.duck.interface';
import {
    CASH,
    FINANCE,
    DealService,
    CarJourneyType,
    BUDGET_TYPE_CASH,
    BUDGET_TYPE_FINANCE,
    BUDGET_TYPE_RENT,
} from 'src/services';
import { IFinanceQuote } from 'src/services/calculateSummary/calculateSummary.types';
import FinanceCalculatorService from 'src/services/financeQuote/financePrices.service';
import { useCarDetailsDuck } from './useCarDetailsDuck';
import { useExternalCheckout } from './useExternalCheckout';
import { useJourneyType } from './useJourneyType';
import { useUserDuck } from '@hooks/useUserDuck';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import { dealStatuses } from '../pages/checkout/my-details';
import { ModalVersion, PxVersion } from '../constants/main';
import useTranslations from '@hooks/useTranslations';
import ConfiguratorDuck from '../redux/configurator/configurator.duck';
import { FEATURES_LIST, FeatureSwitchContext } from '../context/featureSwitchApp';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';

const dispatchDealChange = (
    dispatch: Dispatch<any>,
    deal: IDeal<IConfigurableCarExtended>,
    shouldBeFinance: boolean = false,
    financeQuote = {} as unknown as IFinanceQuote,
    isUserLoggedInAsDealer?: boolean,
    hasLeasysSelected?: boolean
) =>
    batch(async () => {
        dispatch(CarDetailsDuck.setCurrentDeal(deal));
        dispatch(CarDetailsDuck.setCurrentCarDetails(deal.fullProductConfiguration));

        // leave finance quote in state if we switch to cash - write to it only finance calls
        if (shouldBeFinance && !hasLeasysSelected) {
            dispatch(FinanceWidgetDuck.setFinanceQuote(financeQuote));
        }

        if (hasLeasysSelected) {
            dispatch(FinanceWidgetDuck.setLeasysQuote(financeQuote));
        }

        // we need to change budget type per deal because finance gateway can fail
        dispatch(
            FilterDuck.changeBudgetType(deal.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH ? CASH : FINANCE)
        );
        await checkUsedPromoCode(dispatch, isUserLoggedInAsDealer);
        dispatch(UIDuck.setLoading(-3));
    });

/**
 * creates deal on change of budget type
 * @param readyToCreate if the creation should be done
 */
export const useCreateDealPerJourney = (readyToCreate: boolean = false) => {
    const { paymentJourneyType, isFinance } = useJourneyType();
    const context = useContext(FeatureSwitchContext);
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const financeWidgetParameters = useSelector((state: Redux) => FinanceWidgetDuck.getFinanceWidgetParameters(state));
    const hasLeasysSelected = useSelector((state: Redux) => FilterDuck.getOwnState(state).hasLeasysSelected);
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);
    const { externalCheckoutEnabled } = useExternalCheckout();
    const { isUserLoggedInAsDealer } = useUserDuck();
    const { isPersonalizedDeal } = usePersonalizedOffer();

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);

    useEffect(() => {
        if (
            !readyToCreate ||
            [
                dealStatuses.redirectedToLoanProvider,
                dealStatuses.financeApproved,
                dealStatuses.financeRejected,
            ].includes(currentDeal?.status)
        ) {
            return;
        }
        const configId = carDetails.stock ? carDetails.orders[0].uuid : carDetails._id ?? carDetails.externalId;
        dispatch(UIDuck.setLoading(3));
        const keepPxV2OnDeal = pxVersion === PxVersion.v2 && autobizPxState?.statusId;
        DealService.setConfiguration(
            getSessionIdCookie(),
            configId,
            paymentJourneyType,
            carDetails.stock ? CarJourneyType.STOCK : CarJourneyType.CONFIGURABLE,
            // if we have FW parameters set then we can restore personalization
            {
                financeGatewayParameters: financeWidgetParameters ?? {},
                useExternalCheckout: externalCheckoutEnabled,
                ...getGeolocationExtraFileds(currentDeal),
                hasSelectedPaymentJourney: true,
                PxV2Enabled: pxVersion === PxVersion.v2,
                ...(currentDeal?.partExchangeId && { selectedTradeValue: true }),
                ...(keepPxV2OnDeal && {
                    mopId: autobizPxState.mopId,
                }),
            },
            isUserLoggedInAsDealer || isPersonalizedDeal || keepPxV2OnDeal ? true : null,
            hasLeasysSelected
        )
            .then(({ headers, data }) => {
                if (headers['x-auth-token']) {
                    setSessionIdCookie(headers['x-auth-token']);
                }
                if (data?.success) {
                    const { deal } = data.data ?? {};
                    const hasFinance =
                        isFinance &&
                        deal.journeyType === MySavedDealJourneyType.BUY_ONLINE_FINANCE &&
                        Boolean(deal.financeQuoteId);

                    if (hasFinance) {
                        FinanceCalculatorService.getFinanceQuote(deal.financeQuoteId)
                            .then(({ data: financeQuote }) => {
                                dispatchDealChange(
                                    dispatch,
                                    deal,
                                    true,
                                    financeQuote,
                                    isUserLoggedInAsDealer,
                                    hasLeasysSelected
                                );
                            })
                            .catch(() => {
                                dispatch(UIDuck.setLoading(-3));
                            });
                    } else {
                        dispatchDealChange(dispatch, deal, false, null, isUserLoggedInAsDealer);
                    }
                }
            })
            .catch(() => {
                dispatch(UIDuck.setLoading(-3));

                dispatch(
                    UIDuck.openModal({
                        modalType: UIDuck.MODAL_TYPES.MISSING_PARTS,
                        data: {
                            title: t('modal.defaultError.title'),
                            message: t('modal.defaultError.description'),
                            buttonText: t('modal.defaultError.button'),
                            onClose: () => window.location.reload(),
                        },
                        modalVersion: ModalVersion.v2,
                        modalProperties: {
                            centerModeOnDesktop: true,
                        },
                    })
                );
            });
    }, [paymentJourneyType, hasLeasysSelected]);
};
